'use client';

import cx from 'classnames';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { Button } from '../form/button/Button';
import { IllustrationCustomStar } from '../illustration/CustomStar';
import { Typography } from '../typography/Typography';

import style from './FlightCancellationOfferBanner.module.css';

type FlightCancellationOfferBannerProps = {
    forceMobile?: boolean;
};

export function FlightCancellationOfferBanner({
    forceMobile = false,
}: FlightCancellationOfferBannerProps) {
    const { formatMessage } = useIntl();
    const { generateBrandUrl } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const conditionsUrl = generateBrandUrl('flight_cancellation_guarantee') ?? undefined;
    const title = formatMessage({ id: 'global.flightCancellationOffer.title' });

    const handleKnowMoreClick = () => {
        trackEvent((ampli, defaultProperties) =>
            ampli.flightCancellationConditionsClicked({
                ...defaultProperties,
                block: 'flight_cancellation_guarantee',
            }),
        );
    };

    return (
        <div className={cx(style.banner, { [style.forceMobile]: forceMobile })}>
            <div className={style.mobile}>
                <IllustrationCustomStar className={style.icon} />
                <div className={style.text}>
                    <Typography scale="primary-body-bold" as="p">
                        {title}
                    </Typography>
                    <Typography scale="primary-xs-regular" as="p">
                        {formatMessage({ id: 'global.flightCancellationOffer.shortDescription' })}
                    </Typography>
                    <Button
                        priority="text"
                        reversed
                        href={conditionsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={style.knowMoreCta}
                        onClick={handleKnowMoreClick}
                    >
                        {formatMessage({ id: 'global.cta.knowMore' })}
                    </Button>
                </div>
            </div>

            <div className={style.desktop}>
                <Typography scale="secondary-xxs-bold" as="p" className={style.title}>
                    {title}
                </Typography>
                <div className={style.pillar}>
                    <IllustrationCustomStar className={style.icon} />
                    <Typography scale="primary-body-bold" as="p">
                        {formatMessage({
                            id: 'global.flightCancellationOffer.protectionPillar',
                        })}
                    </Typography>
                </div>
                <div className={style.pillar}>
                    <IllustrationCustomStar className={style.icon} />
                    <Typography scale="primary-body-bold" as="p">
                        {formatMessage({
                            id: 'global.flightCancellationOffer.postponementPillar',
                        })}
                    </Typography>
                </div>
                <div className={style.ctaCol}>
                    <Typography scale="primary-xxs-regular" as="p" className={style.conditions}>
                        {formatMessage({ id: 'global.flightCancellationOffer.conditions' })}
                    </Typography>
                    <Button
                        priority="text"
                        reversed
                        href={conditionsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={style.knowMoreCta}
                        onClick={handleKnowMoreClick}
                    >
                        {formatMessage({ id: 'global.cta.knowMore' })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
